// Background blur for the outgoing camera picture. Two tiers:
//
//  1. Native — the browser/OS applies blur inside the camera pipeline
//     (Windows Studio Effects, macOS, some webcams). Chromium exposes this
//     as the `backgroundBlur` track capability. Near-zero cost; used when
//     available.
//  2. Software — MediaPipe Selfie Segmentation running as WebAssembly in
//     the page, compositing the person over a blurred copy of the frame on
//     a canvas whose captured stream becomes the outgoing video track.
//
// Everything happens in the participant's browser. The model and WASM are
// served from this site (/vendor/mediapipe, see NOTICE.txt there) so no
// third-party host is ever contacted — a compliance requirement, see
// video-sessions/docs/PLATFORM.md. Browser-only: never construct during render.

// A third option, virtual backgrounds, reuses tier 2's segmentation: the
// person is composited over a still image instead of a blurred frame. The
// images are drawn by this site (BACKGROUND_SCENES, plain SVG data URIs) —
// nothing is fetched, nothing leaves the browser, and there is no photograph
// of anyone's real room to appear behind a therapist.

export type BackgroundMode = "none" | "blur" | `scene:${string}`
export type BackgroundStatus = "off" | "loading" | "native" | "software" | "unsupported" | "too-slow"

export type BackgroundScene = { key: string; label: string; src: string }

// Soft, low-contrast scenes: they sit behind a face at 640px and re-encode
// as video, so fine detail would only smear. Each is a tiny inline SVG.
function scene(key: string, label: string, svg: string): BackgroundScene {
  return { key, label, src: `data:image/svg+xml;utf8,${encodeURIComponent(svg.replace(/\s+/g, " ").trim())}` }
}

const CREAM = "#f3ede4"
const STONE = "#e4ddd2"
const TEAL = "#1b4a52"

export const BACKGROUND_SCENES: readonly BackgroundScene[] = [
  scene("calm", "Calm room", `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1280 720">
      <defs><linearGradient id="w" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${CREAM}"/><stop offset="1" stop-color="${STONE}"/></linearGradient></defs>
      <rect width="1280" height="720" fill="url(#w)"/>
      <rect x="0" y="560" width="1280" height="160" fill="#d6cdbf"/>
      <rect x="0" y="552" width="1280" height="10" fill="#c9bfae"/>
      <rect x="120" y="150" width="340" height="300" rx="6" fill="#ece5da" stroke="#cfc4b3" stroke-width="10"/>
      <rect x="160" y="190" width="260" height="220" rx="3" fill="#dfe8e6"/>
    </svg>`),
  scene("study", "Study", `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1280 720">
      <rect width="1280" height="720" fill="${STONE}"/>
      <g fill="#b8a48a">
        <rect x="80" y="80" width="420" height="560" rx="8"/>
      </g>
      <g fill="#cbbba3"><rect x="100" y="180" width="380" height="14"/><rect x="100" y="330" width="380" height="14"/><rect x="100" y="480" width="380" height="14"/></g>
      <g>
        <rect x="112" y="100" width="40" height="78" fill="#8e5a4a"/><rect x="158" y="96" width="30" height="82" fill="#4a5d6a"/><rect x="194" y="104" width="46" height="74" fill="#a88f5c"/><rect x="246" y="98" width="34" height="80" fill="#6b7a5a"/><rect x="286" y="102" width="52" height="76" fill="#9c6b6b"/><rect x="344" y="94" width="30" height="84" fill="#5b6c7d"/><rect x="380" y="106" width="44" height="72" fill="#c2a06a"/><rect x="430" y="100" width="40" height="78" fill="#7a6a5a"/>
        <rect x="112" y="250" width="50" height="78" fill="#6b7a5a"/><rect x="168" y="246" width="36" height="82" fill="#9c6b6b"/><rect x="210" y="254" width="42" height="74" fill="#4a5d6a"/><rect x="258" y="248" width="30" height="80" fill="#c2a06a"/><rect x="294" y="252" width="56" height="76" fill="#8e5a4a"/><rect x="356" y="244" width="34" height="84" fill="#a88f5c"/><rect x="396" y="256" width="46" height="72" fill="#5b6c7d"/>
        <rect x="112" y="400" width="44" height="78" fill="#5b6c7d"/><rect x="162" y="396" width="52" height="82" fill="#a88f5c"/><rect x="220" y="404" width="30" height="74" fill="#8e5a4a"/><rect x="256" y="398" width="48" height="80" fill="#6b7a5a"/><rect x="310" y="402" width="36" height="76" fill="#c2a06a"/><rect x="352" y="394" width="42" height="84" fill="#9c6b6b"/><rect x="400" y="406" width="40" height="72" fill="#4a5d6a"/>
      </g>
      <rect x="0" y="600" width="1280" height="120" fill="#cbbfae"/>
    </svg>`),
  scene("garden", "Garden window", `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1280 720">
      <rect width="1280" height="720" fill="${CREAM}"/>
      <rect x="740" y="90" width="440" height="440" rx="8" fill="#f8f6f1" stroke="#cfc4b3" stroke-width="14"/>
      <rect x="760" y="110" width="400" height="400" fill="#cfe3d5"/>
      <ellipse cx="880" cy="420" rx="170" ry="90" fill="#8fb497"/>
      <ellipse cx="1060" cy="400" rx="150" ry="110" fill="#7aa384"/>
      <ellipse cx="960" cy="330" rx="120" ry="80" fill="#a4c4a9"/>
      <rect x="957" y="110" width="6" height="400" fill="#e9e3d8"/><rect x="760" y="307" width="400" height="6" fill="#e9e3d8"/>
      <rect x="0" y="580" width="1280" height="140" fill="#d6cdbf"/>
    </svg>`),
  scene("plain", "Soft teal", `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1280 720">
      <defs><radialGradient id="g" cx="0.5" cy="0.45" r="0.8"><stop offset="0" stop-color="#2d6470"/><stop offset="1" stop-color="${TEAL}"/></radialGradient></defs>
      <rect width="1280" height="720" fill="url(#g)"/>
    </svg>`),
]

export function sceneFor(mode: BackgroundMode): BackgroundScene | null {
  if (!mode.startsWith("scene:")) return null
  const key = mode.slice("scene:".length)
  return BACKGROUND_SCENES.find((s) => s.key === key) ?? null
}

const VENDOR_PATH = "/vendor/mediapipe/"
const TARGET_WIDTH = 640 // processing resolution; the model itself runs at 256×144
const FRAME_INTERVAL_MS = 1000 / 24
const SLOW_FRAME_MS = 60 // sustained frames slower than this => give up
const SLOW_WINDOW = 45
// Mask smoothing: each frame's mask is drawn over the running one at this
// alpha, so a pixel needs several frames of agreement to flip — the edge
// stops flickering. Higher = follows movement faster but jitters more.
const MASK_PERSISTENCE = 0.5
const MASK_FEATHER_PX = 3
// The model returns a per-pixel probability, and painting it raw means a
// pixel it is 40% sure about is 40% visible — a beam behind the head shows
// through as a ghost. Snap it: below the threshold is background, full
// stop. The contrast curve does that on the GPU without touching pixels.
const MASK_THRESHOLD = 0.55
const STORAGE_KEY = "sanctuary-video-background"

type MpResults = { segmentationMask: CanvasImageSource; image: CanvasImageSource }
type MpSelfieSegmentation = {
  setOptions(options: { modelSelection?: number; selfieMode?: boolean }): void
  onResults(listener: (results: MpResults) => void): void
  initialize(): Promise<void>
  send(inputs: { image: HTMLVideoElement }): Promise<void>
  close(): Promise<void>
}

declare global {
  interface Window {
    SelfieSegmentation?: new (config: { locateFile?: (file: string) => string }) => MpSelfieSegmentation
  }
}

// ---- preference ------------------------------------------------------------

export function loadBackgroundPreference(): BackgroundMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === "blur") return "blur"
    if (stored && sceneFor(stored as BackgroundMode)) return stored as BackgroundMode
    return "none"
  } catch {
    return "none"
  }
}

export function saveBackgroundPreference(mode: BackgroundMode): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode)
  } catch {
    /* private mode etc. */
  }
}

// ---- tier 1: native -------------------------------------------------------------

type BlurCapabilities = MediaTrackCapabilities & { backgroundBlur?: boolean[] }
type BlurSettings = MediaTrackSettings & { backgroundBlur?: boolean }

export function nativeBlurSupported(track: MediaStreamTrack): boolean {
  try {
    const caps = track.getCapabilities() as BlurCapabilities
    return Array.isArray(caps.backgroundBlur) && caps.backgroundBlur.includes(true)
  } catch {
    return false
  }
}

// Returns true when the track now reports blur in the requested state.
export async function setNativeBlur(track: MediaStreamTrack, on: boolean): Promise<boolean> {
  if (!nativeBlurSupported(track)) return false
  try {
    await track.applyConstraints({ advanced: [{ backgroundBlur: on } as MediaTrackConstraintSet] })
    return (track.getSettings() as BlurSettings).backgroundBlur === on
  } catch {
    return false
  }
}

// ---- tier 2: software -------------------------------------------------------------

let scriptLoad: Promise<void> | null = null

function loadSegmentationScript(): Promise<void> {
  if (window.SelfieSegmentation) return Promise.resolve()
  if (!scriptLoad) {
    scriptLoad = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = `${VENDOR_PATH}selfie_segmentation.js`
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => {
        scriptLoad = null
        reject(new Error("Could not load the segmentation script"))
      }
      document.head.appendChild(script)
    })
  }
  return scriptLoad
}

function supportsCanvasFilter(ctx: CanvasRenderingContext2D): boolean {
  return typeof (ctx as CanvasRenderingContext2D & { filter?: string }).filter === "string"
}

export class SoftwareBlur {
  private readonly video = document.createElement("video")
  private readonly canvas = document.createElement("canvas")
  private readonly person = document.createElement("canvas")
  private readonly small = document.createElement("canvas")
  private readonly ctx: CanvasRenderingContext2D
  private readonly personCtx: CanvasRenderingContext2D
  private readonly smallCtx: CanvasRenderingContext2D
  private readonly useFilter: boolean
  private segmenter: MpSelfieSegmentation | null = null
  private outputStream: MediaStream | null = null
  private running = false
  private busy = false
  private timer: number | null = null
  private frameTimes: number[] = []
  // A decoded scene to paint instead of the blurred frame; null means blur.
  private scene: HTMLImageElement | null = null
  // The mask, smoothed over time. The model re-decides every edge pixel every
  // frame, and behind a blur that jitter is invisible; behind a still image
  // it reads as the outline flickering. Each frame the running mask is faded
  // towards transparent and the new one drawn over it, so the result is a
  // true moving average: a pixel the model stops calling "person" fades out
  // over a few frames. (Drawing without the fade made it a running maximum —
  // everywhere the head had ever been stayed cut out, like an eraser.)
  private readonly mask = document.createElement("canvas")
  private readonly maskCtx: CanvasRenderingContext2D
  // The thresholded, feathered copy of this frame's mask, before blending.
  private readonly fresh = document.createElement("canvas")
  private readonly freshCtx: CanvasRenderingContext2D

  constructor(private readonly onTooSlow: () => void) {
    const ctx = this.canvas.getContext("2d")
    const personCtx = this.person.getContext("2d")
    const smallCtx = this.small.getContext("2d")
    const maskCtx = this.mask.getContext("2d")
    const freshCtx = this.fresh.getContext("2d")
    if (!ctx || !personCtx || !smallCtx || !maskCtx || !freshCtx) throw new Error("Canvas 2D is not available")
    this.ctx = ctx
    this.personCtx = personCtx
    this.smallCtx = smallCtx
    this.maskCtx = maskCtx
    this.freshCtx = freshCtx
    this.useFilter = supportsCanvasFilter(ctx)
    this.video.muted = true
    this.video.playsInline = true
  }

  // Resolves with the processed video track once the first frame can be produced.
  async start(source: MediaStreamTrack): Promise<MediaStreamTrack> {
    await loadSegmentationScript()
    const Ctor = window.SelfieSegmentation
    if (!Ctor) throw new Error("Segmentation library did not initialise")

    const segmenter = new Ctor({ locateFile: (file) => `${VENDOR_PATH}${file}` })
    // 1 = landscape model (256×144). The general model (0) draws a cleaner
    // outline but dropped frames on an ordinary laptop, and a smeared frame
    // is worse than a slightly rough edge.
    segmenter.setOptions({ modelSelection: 1, selfieMode: false })
    segmenter.onResults((results) => this.composite(results))
    await segmenter.initialize()
    this.segmenter = segmenter

    await this.setSource(source)
    this.running = true
    this.tick()

    this.outputStream = this.canvas.captureStream(24)
    return this.outputStream.getVideoTracks()[0]
  }

  // Paint this image behind the person instead of blurring; null returns to blur.
  // Resolves once the image is decoded, so the first frame is never blank.
  async setScene(scene: BackgroundScene | null): Promise<void> {
    if (!scene) {
      this.scene = null
      return
    }
    const img = new Image()
    img.src = scene.src
    await img.decode()
    this.scene = img
  }

  // Swap the camera feeding the effect (device change) without touching the output track.
  async setSource(track: MediaStreamTrack): Promise<void> {
    this.video.srcObject = new MediaStream([track])
    await this.video.play().catch(() => {})
    if (this.video.readyState < 1) {
      await new Promise<void>((resolve) => {
        this.video.onloadedmetadata = () => resolve()
      })
    }
    const srcW = this.video.videoWidth || 1280
    const srcH = this.video.videoHeight || 720
    const w = Math.min(TARGET_WIDTH, srcW)
    const h = Math.round((w * srcH) / srcW)
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = this.person.width = this.mask.width = this.fresh.width = w
      this.canvas.height = this.person.height = this.mask.height = this.fresh.height = h
      this.maskCtx.clearRect(0, 0, w, h)
      this.small.width = Math.max(1, Math.round(w / 10))
      this.small.height = Math.max(1, Math.round(h / 10))
    }
  }

  stop(): void {
    this.running = false
    if (this.timer !== null) window.clearTimeout(this.timer)
    this.timer = null
    this.outputStream?.getTracks().forEach((t) => t.stop())
    this.outputStream = null
    this.segmenter?.close().catch(() => {})
    this.segmenter = null
    this.video.pause()
    this.video.srcObject = null
  }

  private tick = (): void => {
    if (!this.running) return
    // setTimeout (not rAF) so the loop keeps going, throttled, in a background tab.
    this.timer = window.setTimeout(this.tick, FRAME_INTERVAL_MS)
    if (this.busy || !this.segmenter || this.video.readyState < 2) return
    this.busy = true
    const started = performance.now()
    this.segmenter
      .send({ image: this.video })
      .then(() => this.recordFrameTime(performance.now() - started))
      .catch(() => {})
      .finally(() => {
        this.busy = false
      })
  }

  private recordFrameTime(ms: number): void {
    this.frameTimes.push(ms)
    if (this.frameTimes.length < SLOW_WINDOW) return
    const avg = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length
    this.frameTimes = []
    if (avg > SLOW_FRAME_MS) {
      this.stop()
      this.onTooSlow()
    }
  }

  private composite({ segmentationMask, image }: MpResults): void {
    if (!this.running) return
    const { width: w, height: h } = this.canvas
    const ctx = this.ctx

    // Settle the mask: keep most of last frame's, blend in a little of this
    // one, and feather the result. Behind a scene the mask *is* the picture's
    // edge, so it has to hold still.
    // This frame's mask: threshold (a steep contrast curve at MASK_THRESHOLD)
    // then feather, so the soft edge is a few pixels wide rather than the
    // whole band of "maybe" the model returned.
    const f = this.freshCtx
    f.clearRect(0, 0, w, h)
    if (this.useFilter) f.filter = `${thresholdFilter()} blur(${MASK_FEATHER_PX}px)`
    f.drawImage(segmentationMask, 0, 0, w, h)
    f.filter = "none"

    // Moving average: fade the running mask by (1 - persistence), then add
    // this frame at persistence. Both steps are needed — the fade is what lets
    // a pixel come back to "background" once the person has moved away.
    const m = this.maskCtx
    m.globalCompositeOperation = "destination-out"
    m.fillStyle = `rgba(0,0,0,${MASK_PERSISTENCE})`
    m.fillRect(0, 0, w, h)
    m.globalCompositeOperation = "source-over"
    m.globalAlpha = MASK_PERSISTENCE
    m.drawImage(this.fresh, 0, 0)
    m.globalAlpha = 1

    // Person cut-out: smoothed mask (opaque where the model sees a person) ∩ frame.
    const p = this.personCtx
    p.globalCompositeOperation = "source-over"
    p.clearRect(0, 0, w, h)
    p.drawImage(this.mask, 0, 0)
    p.globalCompositeOperation = "source-in"
    p.drawImage(image, 0, 0, w, h)

    // Background: the chosen scene, else a blurred copy of the frame.
    ctx.globalCompositeOperation = "source-over"
    if (this.scene) {
      drawCover(ctx, this.scene, w, h)
    } else if (this.useFilter) {
      ctx.filter = `blur(${Math.round(w / 45)}px)`
      ctx.drawImage(image, 0, 0, w, h)
      ctx.filter = "none"
    } else {
      // No canvas filter support (older Safari): down-/up-sample for a cheap blur.
      this.smallCtx.drawImage(image, 0, 0, this.small.width, this.small.height)
      ctx.imageSmoothingEnabled = true
      ctx.drawImage(this.small, 0, 0, w, h)
    }

    ctx.drawImage(this.person, 0, 0)
  }
}


// object-fit: cover — scale the image to fill w×h, cropping the long side.
function drawCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement, w: number, h: number): void {
  const iw = img.naturalWidth || 1280
  const ih = img.naturalHeight || 720
  const scale = Math.max(w / iw, h / ih)
  const dw = iw * scale
  const dh = ih * scale
  ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh)
}

// A CSS contrast() steep enough to act as a threshold: values below
// MASK_THRESHOLD go to 0, above it to 1, with a narrow ramp between. The
// mask is a luminance/alpha image, so contrast on it is exactly a cut.
function thresholdFilter(): string {
  // contrast(c) maps v → (v - 0.5) * c + 0.5; shift the midpoint first with
  // brightness so the cut lands at MASK_THRESHOLD rather than at 0.5.
  const shift = 0.5 / MASK_THRESHOLD
  return `brightness(${shift.toFixed(3)}) contrast(12)`
}
